import { useCallback, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Space,
  message,
  Modal,
} from "antd";
import { ReloadOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import usePermission from "../../hooks/usePermission";
import { PartnerLabel } from "../../components/common/DeletedTag";
import {
  listApprovedTransactions,
  updateTransaction,
  deleteTransaction,
  exportTransactions,
} from "../../services/transactions.service";

const { RangePicker } = DatePicker;

function formatOmr(v) {
  return Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function ApprovedTab() {
  const { canEdit, canDelete } = usePermission("transactions");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [month, setMonth] = useState(undefined);
  const [editModal, setEditModal] = useState({ open: false, record: null });
  const [editForm] = Form.useForm();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: 10,
        search: search || undefined,
      };
      if (dateRange?.[0] && dateRange?.[1]) {
        params.fromDate = dateRange[0].format("YYYY-MM-DD");
        params.toDate = dateRange[1].format("YYYY-MM-DD");
      }
      if (month) {
        params.month = month.month() + 1;
        params.year = month.year();
      }
      const res = await listApprovedTransactions(params);
      setRows(res?.data?.records || []);
      setTotal(res?.data?.pagination?.total_count || 0);
    } catch {
      message.error("Failed to load approved transactions");
    } finally {
      setLoading(false);
    }
  }, [page, search, dateRange, month]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openEdit = (record) => {
    editForm.setFieldsValue({
      transactionDate: record.transactionDate ? dayjs(record.transactionDate) : null,
      amountCollected: record.amountCollected,
      ordersCount: record.ordersCount,
      notes: record.notes,
    });
    setEditModal({ open: true, record });
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      await updateTransaction(editModal.record._id, {
        transactionDate: values.transactionDate
          ? values.transactionDate.format("YYYY-MM-DD")
          : undefined,
        amountCollected: values.amountCollected,
        ordersCount: values.ordersCount,
        notes: values.notes,
      });
      message.success("Updated");
      setEditModal({ open: false, record: null });
      fetchList();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.errors?.[0]?.msg || "Update failed");
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Delete approved transaction?",
      content: `${record.partnerName} — ${record.orderId}`,
      okType: "danger",
      onOk: async () => {
        try {
          await deleteTransaction(record._id);
          message.success("Deleted");
          fetchList();
        } catch {
          message.error("Delete failed");
        }
      },
    });
  };

  const handleExport = async () => {
    try {
      const params = { list: "approved", search: search || undefined };
      if (dateRange?.[0] && dateRange?.[1]) {
        params.fromDate = dateRange[0].format("YYYY-MM-DD");
        params.toDate = dateRange[1].format("YYYY-MM-DD");
      }
      if (month) {
        params.month = month.month() + 1;
        params.year = month.year();
      }
      const blob = await exportTransactions(params);
      downloadBlob(blob, `Transactions_approved_${dayjs().format("YYYY-MM-DD")}.csv`);
    } catch {
      message.error("Export failed");
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "transactionDateLabel",
      width: 120,
      render: (v) => v || "—",
    },
    {
      title: "Rider ID",
      dataIndex: "partnerId",
      width: 130,
      render: (v, record) => (
        <PartnerLabel name={v || "—"} deleted={record.riderDeleted} />
      ),
    },
    {
      title: "Rider Name",
      dataIndex: "partnerName",
      render: (v, record) => <PartnerLabel name={v} deleted={record.riderDeleted} />,
    },
    {
      title: "Total Orders",
      dataIndex: "ordersCount",
      width: 110,
      align: "right",
      render: (v) => v ?? 0,
    },
    {
      title: "Total Amount (OMR)",
      dataIndex: "amountCollected",
      align: "right",
      render: formatOmr,
    },
    { title: "Source", dataIndex: "sourceLabel", width: 100, render: (v) => v || "—" },
    { title: "Approved By", dataIndex: "approvedByName" },
    {
      title: "Approval Date",
      dataIndex: "approvedAt",
      render: (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "—"),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Search rider / order"
            style={{ width: 220 }}
            onSearch={(v) => {
              setPage(1);
              setSearch(v);
            }}
          />
          <RangePicker
            value={dateRange}
            onChange={(v) => {
              setPage(1);
              setDateRange(v);
            }}
            format="DD-MM-YYYY"
          />
          <DatePicker
            picker="month"
            value={month}
            onChange={(v) => {
              setPage(1);
              setMonth(v);
            }}
            format="MMM YYYY"
          />
          <Button icon={<ReloadOutlined />} onClick={fetchList}>
            Refresh
          </Button>
        </Space>
        <Button icon={<DownloadOutlined />} onClick={handleExport}>
          Export CSV
        </Button>
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize: 10, total }}
        onPageChange={(p) => setPage(p)}
        actions={{
          onEdit: canEdit ? openEdit : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title="Edit Approved Transaction"
        open={editModal.open}
        onCancel={() => setEditModal({ open: false, record: null })}
        onOk={handleEditSave}
        okText="Save"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="transactionDate" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
          </Form.Item>
          <Form.Item
            name="amountCollected"
            label="Total Amount (OMR)"
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} step={0.001} precision={3} />
          </Form.Item>
          <Form.Item name="ordersCount" label="Total Number of Orders">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
